/**
 * Upsell Teasers Component
 * AI-generated recommendations for modules/features not yet enabled
 * Shows brief insights based on business data to demonstrate value
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Sparkles, TrendingUp, Users, DollarSign, Target, ShieldCheck,
  BarChart3, Clock, AlertTriangle, ChevronRight, Lock, Unlock,
  Lightbulb, Zap, ArrowUpRight, Package, Truck, Calendar
} from 'lucide-react';

// Module teaser configurations with industry-specific insights
const MODULE_TEASERS = {
  inventoryManagement: {
    icon: Package,
    title: 'Smart Inventory',
    gradient: 'from-amber-500 to-orange-500',
    insights: {
      wholesale: {
        headline: 'Your seasonal patterns suggest opportunity',
        detail: 'Pool products typically see 340% demand increase Nov-Feb. Smart reorder points could prevent stockouts.',
        metric: '23%',
        metricLabel: 'potential margin improvement',
        opportunity: '$45,000 in prevented stockouts annually'
      },
      hospitality: {
        headline: 'Menu profitability insights waiting',
        detail: 'Your top 20% of items likely drive 80% of profits. Identify your stars vs. puzzles.',
        metric: '15%',
        metricLabel: 'food cost reduction potential',
        opportunity: 'Optimize supplier orders automatically'
      },
      retail: {
        headline: 'Dead stock detection available',
        detail: 'Industry benchmarks show 8-12% of inventory becomes obsolete. Track and prevent.',
        metric: '$12K',
        metricLabel: 'in recoverable inventory',
        opportunity: 'AI-powered demand forecasting'
      },
      qsr: {
        headline: 'Food waste & COGS intelligence',
        detail: 'Track daily protein usage vs sales. Identify over-prepping patterns that cost $800+/week.',
        metric: '9%',
        metricLabel: 'food cost reduction',
        opportunity: 'Automated par-level management'
      },
      default: {
        headline: 'Inventory intelligence ready',
        detail: 'Track stock levels, automate reorders, and reduce carrying costs.',
        metric: '18%',
        metricLabel: 'average cost reduction',
        opportunity: 'Real-time stock visibility'
      }
    }
  },
  staffScheduling: {
    icon: Users,
    title: 'Staff Intelligence',
    gradient: 'from-blue-500 to-indigo-500',
    insights: {
      hospitality: {
        headline: 'Labor cost optimization detected',
        detail: 'Your busiest hours may be under-staffed while quiet periods over-staffed.',
        metric: '12%',
        metricLabel: 'labor cost savings potential',
        opportunity: 'AI scheduling based on demand patterns'
      },
      healthcare: {
        headline: 'Appointment-staff matching available',
        detail: 'Match provider availability to patient demand for optimal throughput.',
        metric: '8+',
        metricLabel: 'additional appointments/week',
        opportunity: 'Reduce patient wait times'
      },
      retail: {
        headline: 'Peak time coverage gaps detected',
        detail: 'Correlate sales patterns with staff schedules for optimal coverage.',
        metric: '15%',
        metricLabel: 'conversion improvement',
        opportunity: 'Right people at right times'
      },
      qsr: {
        headline: 'SPLH-optimized rostering available',
        detail: 'Match crew to demand curves. Your 12-1pm rush needs 3 more DT crew; 2-4pm has 4 excess hours.',
        metric: '+$8',
        metricLabel: 'SPLH improvement',
        opportunity: 'AI rostering from POS data'
      },
      default: {
        headline: 'Workforce optimization ready',
        detail: 'Balance staff costs with service levels using data-driven scheduling.',
        metric: '10%',
        metricLabel: 'efficiency improvement',
        opportunity: 'Automated roster generation'
      }
    }
  },
  advancedAnalytics: {
    icon: BarChart3,
    title: 'Predictive Analytics',
    gradient: 'from-purple-500 to-pink-500',
    insights: {
      wholesale: {
        headline: 'Demand forecasting available',
        detail: 'Predict retailer orders based on seasonality, trends, and market conditions.',
        metric: '92%',
        metricLabel: 'forecast accuracy',
        opportunity: 'Reduce emergency orders by 60%'
      },
      professional: {
        headline: 'Revenue prediction models ready',
        detail: 'Forecast billings, identify at-risk clients, and optimize capacity.',
        metric: '3mo',
        metricLabel: 'forward visibility',
        opportunity: 'Pipeline health monitoring'
      },
      default: {
        headline: 'Future-focused insights available',
        detail: 'Move from reactive to proactive with AI-powered predictions.',
        metric: '85%',
        metricLabel: 'prediction accuracy',
        opportunity: 'Strategic decision support'
      }
    }
  },
  complianceAutomation: {
    icon: ShieldCheck,
    title: 'Compliance Center',
    gradient: 'from-emerald-500 to-[#0066CC]',
    insights: {
      healthcare: {
        headline: 'AHPRA compliance tracking available',
        detail: 'Automate registration renewals, CPD tracking, and audit preparation.',
        metric: '40hrs',
        metricLabel: 'saved per quarter',
        opportunity: 'Eliminate compliance surprises'
      },
      hospitality: {
        headline: 'Food safety automation ready',
        detail: 'Digital temp logs, HACCP tracking, and inspection preparation.',
        metric: '100%',
        metricLabel: 'audit readiness',
        opportunity: 'Automated compliance alerts'
      },
      construction: {
        headline: 'WHS documentation available',
        detail: 'Site safety tracking, incident reports, and contractor compliance.',
        metric: '90%',
        metricLabel: 'documentation coverage',
        opportunity: 'Reduce compliance risk'
      },
      default: {
        headline: 'Regulatory tracking ready',
        detail: 'Stay ahead of compliance requirements with automated monitoring.',
        metric: '24/7',
        metricLabel: 'compliance monitoring',
        opportunity: 'Peace of mind protection'
      }
    }
  },
  supplierManagement: {
    icon: Truck,
    title: 'Supplier Intelligence',
    gradient: 'from-[#007AFF] to-blue-500',
    insights: {
      wholesale: {
        headline: 'Supplier performance data available',
        detail: 'Track lead times, quality scores, and negotiate from data.',
        metric: '8%',
        metricLabel: 'better terms achievable',
        opportunity: 'Automated reorder optimization'
      },
      hospitality: {
        headline: 'Vendor consolidation opportunities',
        detail: 'Identify overlap, compare pricing, and streamline ordering.',
        metric: '12%',
        metricLabel: 'procurement savings',
        opportunity: 'Reduce supplier complexity'
      },
      default: {
        headline: 'Supply chain visibility ready',
        detail: 'Single view of all suppliers, orders, and performance.',
        metric: '15%',
        metricLabel: 'cost reduction potential',
        opportunity: 'Smarter procurement decisions'
      }
    }
  },
  driveThruAnalytics: {
    icon: Clock,
    title: 'Drive-Through AI',
    gradient: 'from-amber-500 to-red-500',
    insights: {
      qsr: {
        headline: 'Wait time prediction models available',
        detail: 'AI predicts demand surges 30 min ahead. Pre-stage top items and deploy DT crew before the rush hits.',
        metric: '-45s',
        metricLabel: 'avg wait reduction',
        opportunity: 'Real-time DT queue optimization'
      },
      default: {
        headline: 'Service speed analytics',
        detail: 'Track and optimize customer wait times across service channels.',
        metric: '25%',
        metricLabel: 'faster service',
        opportunity: 'Queue management AI'
      }
    }
  },
  multiLocationBenchmark: {
    icon: Target,
    title: 'Location Benchmarking',
    gradient: 'from-violet-500 to-purple-500',
    insights: {
      qsr: {
        headline: 'Cross-store SPLH gap analysis ready',
        detail: 'Footscray SPLH is 27% below Southport. Identify exact shift patterns, menu mix, and staffing differences driving the gap.',
        metric: '+$6',
        metricLabel: 'SPLH recoverable',
        opportunity: 'Best-practice sharing across stores'
      },
      default: {
        headline: 'Multi-site performance comparison',
        detail: 'Benchmark locations against each other and industry standards.',
        metric: '18%',
        metricLabel: 'performance variance',
        opportunity: 'Identify top practices to replicate'
      }
    }
  }
};

// Get enabled modules from user context
const getUpsellOpportunities = (enabledModules = [], industry = 'default', industryData = {}) => {
  const enabledSet = new Set(enabledModules.map(m => m?.toLowerCase()));
  const opportunities = [];
  
  Object.entries(MODULE_TEASERS).forEach(([moduleId, config]) => {
    // Check if module is not enabled
    if (!enabledSet.has(moduleId.toLowerCase())) {
      const industryInsight = config.insights[industry] || config.insights.default;
      opportunities.push({
        id: moduleId,
        ...config,
        insight: industryInsight
      });
    }
  });
  
  // Return top 3 most relevant
  return opportunities.slice(0, 3);
};

const UpsellTeaserCard = ({ teaser, onExplore }) => {
  const Icon = teaser.icon;
  
  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-all overflow-hidden group">
      <div className={`h-1 bg-gradient-to-r ${teaser.gradient}`} />
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${teaser.gradient} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-slate-900 text-sm">{teaser.title}</h4>
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                <Lock className="w-2.5 h-2.5 mr-0.5" />
                Upgrade
              </Badge>
            </div>
            <p className="text-xs font-medium text-slate-700 mb-1">
              {teaser.insight.headline}
            </p>
            <p className="text-xs text-slate-500 line-clamp-2 mb-3">
              {teaser.insight.detail}
            </p>
            
            {/* Metric highlight */}
            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-2 mb-3">
              <div>
                <span className={`text-lg font-bold bg-gradient-to-r ${teaser.gradient} bg-clip-text text-transparent`}>
                  {teaser.insight.metric}
                </span>
                <span className="text-xs text-slate-500 ml-1">{teaser.insight.metricLabel}</span>
              </div>
              <Sparkles className="w-4 h-4 text-amber-500" />
            </div>
            
            {/* Opportunity */}
            <div className="flex items-center gap-1 text-xs text-emerald-600 mb-3">
              <TrendingUp className="w-3 h-3" />
              <span>{teaser.insight.opportunity}</span>
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full text-xs group-hover:bg-slate-50"
              onClick={() => onExplore(teaser.id)}
            >
              Explore {teaser.title}
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const UpsellTeasers = ({ 
  enabledModules = [], 
  industry = 'default', 
  industryData = {},
  onExploreModule,
  className = ''
}) => {
  const opportunities = useMemo(() => 
    getUpsellOpportunities(enabledModules, industry, industryData),
    [enabledModules, industry, industryData]
  );
  
  if (opportunities.length === 0) return null;
  
  const handleExplore = (moduleId) => {
    if (onExploreModule) {
      onExploreModule(moduleId);
    } else {
      // Default: navigate to pricing/upgrade page
      window.location.href = '/pricing?highlight=' + moduleId;
    }
  };
  
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Lightbulb className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">Unlock More Value</h3>
            <p className="text-xs text-slate-500">AI-detected opportunities for your business</p>
          </div>
        </div>
        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
          <Zap className="w-3 h-3 mr-1" />
          {opportunities.length} Available
        </Badge>
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        {opportunities.map(teaser => (
          <UpsellTeaserCard 
            key={teaser.id} 
            teaser={teaser} 
            onExplore={handleExplore}
          />
        ))}
      </div>
      
      {/* Request Private Demo CTA */}
      <div className="mt-4 rounded-xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="request-demo-cta">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white">Want to see these in action for your business?</h4>
            <p className="text-xs text-slate-400">Get a personalised demo tailored to your industry, data, and goals.</p>
          </div>
        </div>
        <Button 
          size="sm"
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold text-xs shrink-0"
          onClick={() => window.location.href = '/contact-us?source=upsell_demo'}
          data-testid="request-demo-btn"
        >
          Request Private Demo
          <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default UpsellTeasers;
